import { com } from '@eidu/web'
import { anchorTypeIdOf } from '../../components/permissions/utils'
import actionLabel from './actionLabel'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import EntitySelectorPermission = com.eidu.sharedlib.entity.permission.EntitySelectorPermission
import GlobalPermission = com.eidu.sharedlib.entity.permission.GlobalPermission
import Action = com.eidu.sharedlib.entity.permission.Action
import Permission = com.eidu.sharedlib.entity.permission.Permission

export type ActionsBreakdown = {
  global: string[]
  perType: { typeName: string; labels: string[] }[]
}

const GLOBAL_ACTION_ORDER: Action[] = [
  ...GlobalAction.values(),
  EntityAction.SubmitForm,
  EntityAction.ViewFormSubmissions,
  EntityAction.ShareSchoolLogin,
]

const ENTITY_ACTION_ORDER: EntityAction[] = [
  EntityAction.CreateEntity,
  EntityAction.ReadEntity,
  EntityAction.UpdateEntity,
  EntityAction.DeleteEntity,
  EntityAction.PublishAsset,
]

const FORM_GLOBAL_ACTIONS: Set<EntityAction> = new Set([
  EntityAction.SubmitForm,
  EntityAction.ViewFormSubmissions,
  EntityAction.ShareSchoolLogin,
])

const ALL_TYPES_KEY = '__all__'
const ALL_TYPES_LABEL = 'All'

const computeActionsBreakdown = (
  permissions: ReadonlyArray<Permission>,
  entityTypes: ReadonlyMap<EntityTypeId, EntityType | null>
): ActionsBreakdown => {
  const globalSet = new Set<Action>()
  const perTypeActions = new Map<string, { typeName: string; actions: Set<EntityAction> }>()
  const types = Array.from(entityTypes.values()).filter((t): t is EntityType => t !== null)

  for (const permission of permissions) {
    if (permission instanceof GlobalPermission) {
      globalSet.add(permission.action)
      continue
    }
    if (permission instanceof EntitySelectorPermission && EntitySelector.All.equals(permission.targets)) {
      if (FORM_GLOBAL_ACTIONS.has(permission.action)) {
        globalSet.add(permission.action)
      } else {
        const entry = perTypeActions.get(ALL_TYPES_KEY) ?? {
          typeName: ALL_TYPES_LABEL,
          actions: new Set<EntityAction>(),
        }
        entry.actions.add(permission.action as EntityAction)
        perTypeActions.set(ALL_TYPES_KEY, entry)
      }
      continue
    }
    const anchor = anchorTypeIdOf(permission, types)
    if (!anchor) continue
    const idStr = anchor.toString()
    const entry = perTypeActions.get(idStr) ?? {
      typeName: String(entityTypes.get(anchor)?.name ?? `Unknown (${idStr})`),
      actions: new Set<EntityAction>(),
    }
    entry.actions.add(permission.action as EntityAction)
    perTypeActions.set(idStr, entry)
  }

  const global = GLOBAL_ACTION_ORDER.filter((a) => globalSet.has(a)).map(actionLabel)
  const perType = Array.from(perTypeActions.values())
    .map(({ typeName, actions }) => ({
      typeName,
      labels: ENTITY_ACTION_ORDER.filter((a) => actions.has(a)).map(actionLabel),
    }))
    .sort((a, b) => {
      if (a.typeName === ALL_TYPES_LABEL) return -1
      if (b.typeName === ALL_TYPES_LABEL) return 1
      return a.typeName.localeCompare(b.typeName)
    })

  return { global, perType }
}

export default computeActionsBreakdown
